import { useContext, useEffect } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  dataLocationToPath,
} from '@ea-lab/reactive-json'

/**
 * Reçoit les données poussées par la page HÔTE qui encadre cet écran, et les
 * écrit dans le store. Un pont, et rien d'autre : ce qui arrive devient de la
 * donnée reactive-json, que le YAML manipule ensuite comme n'importe quelle
 * autre.
 *
 * Pourquoi un composant (échappatoire config-first) : un écran encadré par un
 * tiers — le panneau Gmail dans son extension — n'a aucun autre moyen de
 * recevoir son contexte. Le `MessageListener` du cœur ne convient pas : il
 * REFUSE les messages d'une autre origine (`event.origin !== location.origin`)
 * et ne sert qu'à déclencher une réaction sur un message dont la charge est
 * exactement égale à une valeur attendue — il ne transporte rien.
 *
 * L'alternative était la query string, qui impose de tout recharger à chaque
 * changement de contexte, limite les données à ce qui tient dans une URL, et
 * les aplatit en chaînes : une liste d'objets n'y survit pas, or `Switch`
 * n'itère que sur des objets. Un seul pont générique remplace donc autant de
 * rustines qu'il y aurait de données à faire passer.
 *
 * SÉCURITÉ. Un message peut venir de n'importe qui : on n'accepte que ceux dont
 * l'origine commence par `originPrefix` ET dont `data.type` vaut `messageType`
 * — nommé ainsi, et non `type`, parce que reactive-json RÉSERVE `type` au nom
 * du composant : les deux s'écraseraient.
 * Sans `originPrefix`, rien n'est accepté — une valeur par défaut permissive
 * ferait de ce pont une porte ouverte sur le store. La charge utile écrite est
 * `data.payload`, jamais le message entier, pour qu'un champ `type` ne se
 * retrouve pas dans les données.
 *
 * YAML :
 *   - type: HostMessageData
 *     messageType: "orbit-gmail-context"
 *     originPrefix: "chrome-extension://"
 *     path: ~~.gmail
 *     ready: ~~.gmailReady      # optionnel : passe à true au premier message
 *
 * Enregistré dans [App.jsx](../../../App.jsx) sous `customPlugins.element`.
 */
export const HostMessageData = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const gdc = globalDataContext?.getRootContext
    ? globalDataContext.getRootContext()
    : globalDataContext

  const resolve = (value) =>
    evaluateTemplateValue({ valueToEvaluate: value, globalDataContext: gdc, templateContext })

  const messageType = resolve(props?.messageType)
  const originPrefix = resolve(props?.originPrefix)
  const pathProp = props?.path
  const readyProp = props?.ready

  useEffect(() => {
    // Sans destination ni origine autorisée, le pont ne s'ouvre pas du tout.
    if (!messageType || !originPrefix || typeof pathProp !== 'string') return undefined

    const toPath = (dataLocation) =>
      dataLocationToPath({
        dataLocation,
        currentPath: templateContext?.templatePath,
        globalDataContext: gdc,
        templateContext,
      })

    const path = toPath(pathProp)
    if (!path) return undefined
    const readyPath = typeof readyProp === 'string' ? toPath(readyProp) : null

    const onMessage = (event) => {
      if (typeof event.origin !== 'string' || !event.origin.startsWith(originPrefix)) return
      if (event.data?.type !== messageType) return
      const payload = event.data?.payload
      if (payload === undefined) return
      gdc.updateData(payload, path)
      if (readyPath) gdc.updateData(true, readyPath)
    }

    window.addEventListener('message', onMessage)

    // ANNONCER qu'on écoute. Sans cela, l'hôte envoie son contexte au
    // chargement du document, AVANT que React ait monté ce composant : le
    // message part dans le vide et l'écran reste sans contexte, sans erreur.
    // C'est l'écran qui sait quand il est prêt — pas l'hôte.
    try {
      window.parent?.postMessage({ type: `${messageType}-ready` }, '*');
    }
    catch { /* pas de parent accessible : rien à annoncer */ void 0; }

    return () => window.removeEventListener('message', onMessage)
  }, [messageType, originPrefix, pathProp, readyProp, gdc, templateContext])

  // Un pont ne s'affiche pas.
  return null
}

export default HostMessageData
